/**
 * ==========================================================================
 * INVOICE EDITOR PAGE - Main Invoice Creation & Editing Component
 * ==========================================================================
 * 
 * This is the core component of the application where invoices are created
 * and edited. It handles complex state management for a multi-section form.
 * 
 * KEY CONCEPTS:
 * 
 * 1. REACT HOOKS USED:
 *    - useState: Manages component state (invoice data, loading states)
 *    - useEffect: Handles side effects (data fetching on mount)
 *    - useRef: References to DOM elements (signature canvas)
 *    - useCallback: Memoizes functions to prevent unnecessary re-renders
 *    - useParams: Extracts URL parameters (/invoices/:id)
 *    - useNavigate: Programmatic navigation
 * 
 * 2. DATA FLOW:
 *    - Fetch initial data (customers, settings, invoice if editing)
 *    - User fills form -> State updates -> Auto-save triggers
 *    - Save sends data to backend API
 *    - PDF generation happens server-side
 * 
 * 3. AUTO-SAVE:
 *    - Debounced saving (2 second delay after changes stop)
 *    - Only triggers for existing invoices (not new ones)
 *    - Uses setTimeout/clearTimeout pattern
 * 
 * 4. CALCULATED FIELDS:
 *    - Totals (parts, labor, subtotal, tax, total due)
 *    - These are computed from state, not stored
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";  // Digital signature capture
import { 
  Save, 
  Download, 
  Plus, 
  Trash2, 
  ArrowLeft,
  Eraser,
  Loader2,
  Truck,
  History
} from "lucide-react";  // Icon library
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";  // Toast notifications

// ==========================================================================
// CONSTANTS
// ==========================================================================

// API base URL from environment variable
// IMPORTANT: Always use environment variables for URLs, never hardcode!
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default logo URL (fallback if settings don't have one)
const DEFAULT_LOGO_URL = "https://customer-assets.emergentagent.com/job_truck-invoice-app/artifacts/zt0c91a2_oproadservice%20contact%20header.jpeg";

// ==========================================================================
// DEFAULT DATA STRUCTURES
// ==========================================================================

/**
 * Empty line item template
 * Each row can have BOTH parts AND labor fields
 * This allows side-by-side display on the invoice
 */
const emptyItem = {
  id: "",
  parts_qty: 0,
  parts_description: "",
  parts_price: 0,
  labor_qty: 0,
  labor_description: "",
  labor_price: 0
};

/**
 * Empty vehicle info template
 * Contains all fields for the vehicle being serviced
 */
const emptyVehicle = {
  truck_number: "",
  trailer_number: "",
  make: "",
  type_model: "",
  year: "",
  serial_vin: "",
  odometer: "",
  license_number: "",
  received_date: "",
  promised_date: "",
  phone_when_ready: false,
  order_written_by: ""
};

/**
 * Empty invoice template
 * This is the initial state for a new invoice
 * 
 * Note: crypto.randomUUID() generates a unique ID client-side
 * This is fine for temporary IDs; the server generates the real ID on save
 */
const emptyInvoice = {
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  customer_address: "",
  second_contact: "",
  vehicle_info: { ...emptyVehicle },  // Spread to create new object
  items: [{ ...emptyItem, id: crypto.randomUUID() }],  // Start with one empty row
  road_service_fee: 0,
  storage_fee: 0,
  tax_rate: 8.25,  // Default tax rate
  signature_data: "",  // Base64 encoded signature image
  signature_date: "",
  status: "estimate",  // "estimate", "awaiting_payment", or "paid"
  estimate_date: "",  // Date when estimate was given (for backdating)
  paid_date: "",  // Date when invoice was marked as paid
  notes: "",
  payment_method: "",  // "cash", "check", "card", "zelle", "other"
  payment_method_other: ""  // Custom payment method text
};

// ==========================================================================
// MAIN COMPONENT
// ==========================================================================

export default function InvoiceEditor() {
  // ==========================================================================
  // HOOKS - URL Parameters & Navigation
  // ==========================================================================
  
  // useParams extracts :id from the URL /invoices/:id
  // id is undefined for new invoices, populated for editing
  const { id } = useParams();
  
  // useNavigate returns a function for programmatic navigation
  // Example: navigate("/invoices") redirects to invoice list
  const navigate = useNavigate();
  
  // useRef creates a persistent reference to the signature canvas
  // Unlike state, changing a ref doesn't trigger re-render
  const signatureRef = useRef(null);
  
  // ==========================================================================
  // STATE MANAGEMENT
  // ==========================================================================
  
  // Main invoice data - this is the "source of truth" for the form
  const [invoice, setInvoice] = useState(emptyInvoice);
  
  // List of customers for the dropdown selector
  const [customers, setCustomers] = useState([]);
  
  // List of vehicles for quick selection (filtered by customer)
  const [vehicles, setVehicles] = useState([]);
  
  // Selected vehicle ID for the dropdown
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  
  // Vehicle repair history
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // Saving vehicle state
  const [savingVehicle, setSavingVehicle] = useState(false);
  
  // App settings (default tax rate, company name, logo, etc.)
  const [settings, setSettings] = useState({ default_tax_rate: 8.25, logo_url: DEFAULT_LOGO_URL });
  
  // Loading state for initial data fetch
  const [loading, setLoading] = useState(true);
  
  // Saving state to show loading spinner on save button
  const [saving, setSaving] = useState(false);
  
  // Timer reference for auto-save debouncing (use ref to avoid re-renders)
  const autoSaveTimerRef = useRef(null);
  
  // Track if currently typing to prevent auto-save interference
  const isTypingRef = useRef(false);

  // ==========================================================================
  // EFFECT HOOKS
  // ==========================================================================
  
  /**
   * Initial data loading effect
   * Runs when component mounts or when `id` changes
   * 
   * The cleanup function clears any pending auto-save timer
   * This prevents memory leaks and stale saves
   */
  useEffect(() => {
    fetchInitialData();
    
    // Cleanup function - runs when component unmounts or before re-run
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [id]);  // Dependency array - re-run when id changes

  // ==========================================================================
  // DATA FETCHING FUNCTIONS
  // ==========================================================================
  
  /**
   * Fetch all initial data needed for the editor
   * 
   * Uses Promise.all for parallel fetching - faster than sequential!
   * Fetches: customers list, vehicles list, app settings, and invoice (if editing)
   */
  const fetchInitialData = async () => {
    try {
      // Parallel fetch of customers, vehicles, and settings
      const [customersRes, vehiclesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/settings`)
      ]);
      
      setCustomers(customersRes.data);
      setVehicles(vehiclesRes.data);
      setSettings(settingsRes.data);
      
      // If we have an ID, we're editing - fetch the invoice
      if (id) {
        const invoiceRes = await axios.get(`${API}/invoices/${id}`);
        setInvoice(invoiceRes.data);
        
        // Restore signature to canvas if it exists
        // The signature is stored as a base64 data URL
        if (invoiceRes.data.signature_data && signatureRef.current) {
          signatureRef.current.fromDataURL(invoiceRes.data.signature_data);
        }
      } else {
        // New invoice - set default tax rate from settings
        setInvoice(prev => ({
          ...prev,
          tax_rate: settingsRes.data.default_tax_rate || 8.25
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      // Always set loading to false, even on error
      setLoading(false);
    }
  };

  // ==========================================================================
  // EVENT HANDLERS
  // ==========================================================================
  
  /**
   * Handle customer selection from dropdown
   * 
   * When a customer is selected, we copy their info to the invoice
   * This "denormalizes" the data - even if customer info changes later,
   * the invoice keeps the info as it was when created
   */
  const handleCustomerSelect = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setInvoice(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone || "",
        customer_address: customer.address || "",
        second_contact: customer.second_contact || ""
      }));
      
      // Fetch only vehicles assigned to this customer
      try {
        const vehiclesRes = await axios.get(`${API}/vehicles?customer_id=${customerId}`);
        setVehicles(vehiclesRes.data);
        setSelectedVehicleId(""); // Reset vehicle selection
      } catch (error) {
        console.error("Error fetching customer vehicles:", error);
        setVehicles([]);
      }
    }
  };

  /**
   * Save current vehicle info as a new vehicle linked to the customer
   */
  const handleSaveAsVehicle = async () => {
    if (!invoice.customer_id) {
      toast.error("Please select a customer first");
      return;
    }
    
    const vehicleInfo = invoice.vehicle_info;
    if (!vehicleInfo.truck_number && !vehicleInfo.trailer_number) {
      toast.error("Please enter a Truck # or Trailer #");
      return;
    }
    
    setSavingVehicle(true);
    try {
      const vehicleData = {
        customer_id: invoice.customer_id,
        customer_name: invoice.customer_name,
        truck_number: vehicleInfo.truck_number || "",
        trailer_number: vehicleInfo.trailer_number || "",
        make: vehicleInfo.make || "",
        model: vehicleInfo.type_model || "",
        year: vehicleInfo.year || "",
        serial_vin: vehicleInfo.serial_vin || "",
        license_plate: vehicleInfo.license_number || "",
        last_odometer: vehicleInfo.odometer || ""
      };
      
      const response = await axios.post(`${API}/vehicles`, vehicleData);
      
      // Add to local vehicles list and select it
      setVehicles(prev => [...prev, response.data]);
      setSelectedVehicleId(response.data.id);
      
      toast.success("Vehicle saved to fleet!");
    } catch (error) {
      console.error("Error saving vehicle:", error);
      toast.error("Failed to save vehicle");
    } finally {
      setSavingVehicle(false);
    }
  };

  /**
   * Handle vehicle selection from dropdown
   * Auto-fills vehicle info from the selected vehicle record
   */
  const handleVehicleSelect = async (vehicleId) => {
    if (!vehicleId || vehicleId === "none") {
      setSelectedVehicleId("");
      return;
    }
    
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setSelectedVehicleId(vehicleId);
      setInvoice(prev => ({
        ...prev,
        vehicle_info: {
          ...prev.vehicle_info,
          truck_number: vehicle.truck_number || "",
          trailer_number: vehicle.trailer_number || "",
          make: vehicle.make || "",
          type_model: vehicle.model || "",
          year: vehicle.year || "",
          serial_vin: vehicle.serial_vin || "",
          license_number: vehicle.license_plate || "",
          odometer: vehicle.last_odometer || ""
        }
      }));
      
      // Fetch repair history for this vehicle
      try {
        const historyRes = await axios.get(`${API}/vehicles/${vehicleId}/history`);
        setVehicleHistory(historyRes.data);
      } catch (error) {
        console.error("Error fetching vehicle history:", error);
        setVehicleHistory([]);
      }
      
      triggerAutoSave();
    }
  };

  /**
   * Format date for display
   */
  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  /**
   * Handle changes to vehicle info fields
   * 
   * Uses computed property name [field] to update the right field
   * Triggers auto-save after change
   */
  const handleVehicleChange = (field, value) => {
    setInvoice(prev => ({
      ...prev,
      vehicle_info: {
        ...prev.vehicle_info,
        [field]: value  // Computed property name
      }
    }));
    triggerAutoSave();
  };

  /**
   * Handle changes to line item fields
   * 
   * Line items are stored in an array - we need to update the right index
   * Numeric fields (qty, price) are parsed to floats
   */
  const handleItemChange = (index, field, value) => {
    const newItems = [...invoice.items];  // Create copy (immutability!)
    
    // For qty and price fields, keep as string while typing to preserve decimals
    // Only convert to number when value is complete (has valid decimal or is empty)
    if (field.includes("qty") || field.includes("price")) {
      // Allow empty string, numbers, and partial decimals (like "5." or "5.2")
      if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
        newItems[index] = {
          ...newItems[index],
          [field]: value === "" ? 0 : value
        };
      }
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    }
    
    setInvoice(prev => ({ ...prev, items: newItems }));
    triggerAutoSave();
  };

  /**
   * Handle blur event for price/qty fields - convert to proper number
   */
  const handleItemBlur = (index, field) => {
    const newItems = [...invoice.items];
    const currentValue = newItems[index][field];
    
    if (field.includes("qty") || field.includes("price")) {
      // Convert string to number on blur
      const numValue = parseFloat(currentValue) || 0;
      newItems[index] = {
        ...newItems[index],
        [field]: numValue
      };
      setInvoice(prev => ({ ...prev, items: newItems }));
    }
  };

  /**
   * Add a new empty line item row
   * 
   * Each item needs a unique ID for React's key prop
   * crypto.randomUUID() generates a UUID v4
   */
  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem, id: crypto.randomUUID() }]
    }));
  };

  /**
   * Remove a line item row by index
   * 
   * Enforces minimum of 1 row - invoice needs at least one line item
   * filter() creates new array without the removed item
   */
  const removeItem = (index) => {
    if (invoice.items.length <= 1) {
      toast.error("At least one item row is required");
      return;
    }
    const newItems = invoice.items.filter((_, i) => i !== index);
    setInvoice(prev => ({ ...prev, items: newItems }));
    triggerAutoSave();
  };

  // ==========================================================================
  // AUTO-SAVE LOGIC
  // ==========================================================================
  
  /**
   * Debounced auto-save trigger
   * 
   * DEBOUNCING EXPLAINED:
   * Instead of saving on every keystroke, we wait until user stops typing.
   * - Each change clears the previous timer
   * - Sets a new timer for 2 seconds
   * - Only when 2 seconds pass without changes does it save
   * 
   * useCallback memoizes this function so it doesn't recreate on every render
   */
  const triggerAutoSave = useCallback(() => {
    // Clear existing timer if any
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer - using ref doesn't cause re-renders
    autoSaveTimerRef.current = setTimeout(() => {
      // Only auto-save existing invoices (not new ones)
      if (id) {
        handleSave(true);  // true = isAutoSave
      }
    }, 2000);  // 2 second delay
  }, [id]);

  // ==========================================================================
  // SAVE FUNCTIONS
  // ==========================================================================
  
  /**
   * Save invoice to backend
   * 
   * @param isAutoSave - true if triggered by auto-save (suppresses toast)
   * 
   * For new invoices (no id): POST to create
   * For existing invoices: PUT to update
   */
  const handleSave = async (isAutoSave = false) => {
    // Validate required fields
    if (!invoice.customer_id) {
      if (!isAutoSave) toast.error("Please select a customer");
      return;
    }

    setSaving(true);
    try {
      // Get signature data from canvas
      let signatureData = invoice.signature_data;
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        // toDataURL() converts canvas to base64 PNG
        signatureData = signatureRef.current.toDataURL();
      }

      // Prepare invoice data for API
      const invoiceData = {
        ...invoice,
        signature_data: signatureData,
        // Auto-set signature date if signature exists but no date
        signature_date: signatureData && !invoice.signature_date 
          ? new Date().toISOString().split('T')[0]  // YYYY-MM-DD format
          : invoice.signature_date
      };

      let response;
      if (id) {
        // UPDATE existing invoice (PUT)
        response = await axios.put(`${API}/invoices/${id}`, invoiceData);
      } else {
        // CREATE new invoice (POST)
        response = await axios.post(`${API}/invoices`, invoiceData);
        // Navigate to the new invoice's edit page
        // replace: true prevents back button from going to /invoices/new
        navigate(`/invoices/${response.data.id}`, { replace: true });
      }

      // Only update local state after MANUAL save to prevent overwriting user's typing
      // Auto-save runs silently in background without disrupting the UI
      if (!isAutoSave) {
        setInvoice(response.data);
        toast.success("Invoice saved successfully");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      if (!isAutoSave) {
        toast.error("Failed to save invoice");
      }
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================================
  // PDF DOWNLOAD
  // ==========================================================================
  
  /**
   * Download invoice as PDF
   * 
   * The PDF is generated server-side using ReportLab
   * We receive a blob (binary data) and create a download link
   */
  const handleDownloadPDF = async () => {
    if (!id) {
      toast.error("Please save the invoice first");
      return;
    }

    try {
      // responseType: 'blob' tells axios to expect binary data
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a temporary URL for the blob data
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create an invisible link element and click it
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoice_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);  // Free up memory
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  /**
   * Clear the signature canvas
   */
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    // Also clear from state
    setInvoice(prev => ({ ...prev, signature_data: "", signature_date: "" }));
  };

  // ==========================================================================
  // CALCULATED VALUES (Derived State)
  // ==========================================================================
  
  /**
   * These values are computed from state, not stored
   * React will recalculate them whenever invoice.items changes
   * 
   * Using reduce() to sum array values:
   * [1, 2, 3].reduce((sum, val) => sum + val, 0) = 6
   * 
   * V2 UPDATE: Tax is now applied ONLY to parts, not labor
   */
  
  // Sum of all parts: qty × price for each item
  const totalParts = invoice.items.reduce((sum, item) => 
    sum + ((parseFloat(item.parts_qty) || 0) * (parseFloat(item.parts_price) || 0)), 0);
  
  // Sum of all labor: qty × price for each item
  const totalLabor = invoice.items.reduce((sum, item) => 
    sum + ((parseFloat(item.labor_qty) || 0) * (parseFloat(item.labor_price) || 0)), 0);
  
  // Tax amount calculated ONLY on parts (V2: Labor is tax-exempt)
  const taxAmount = totalParts * ((parseFloat(invoice.tax_rate) || 0) / 100);
  
  // Subtotal before tax (parts + labor + fees)
  const subtotal = totalParts + totalLabor + (parseFloat(invoice.road_service_fee) || 0) + (parseFloat(invoice.storage_fee) || 0);
  
  // Final total (subtotal + tax on parts only)
  const totalDue = subtotal + taxAmount;

  // ==========================================================================
  // LOADING STATE
  // ==========================================================================
  
  // Show spinner while fetching initial data
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ==========================================================================
  // RENDER - Main Component JSX
  // ==========================================================================

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-100 min-h-screen" data-testid="invoice-editor-page">
      {/* Header */}
      <div className="no-print flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/invoices")}
              className="gap-1 sm:gap-2 px-2 sm:px-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900 font-['Manrope']">
                {id ? `Invoice ${invoice.invoice_number || ""}` : "New Invoice"}
              </h1>
              {invoice.status && (
                <span className={`text-xs sm:text-sm ${
                  invoice.status === "paid" 
                    ? "text-emerald-600" 
                    : invoice.status === "awaiting_payment"
                    ? "text-blue-600"
                    : "text-amber-600"
                }`}>
                  Status: {invoice.status === "paid" ? "Paid" : invoice.status === "awaiting_payment" ? "Awaiting Payment" : "Estimate"}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select 
            value={invoice.status} 
            onValueChange={(value) => setInvoice(prev => ({ ...prev, status: value }))}
          >
            <SelectTrigger className="w-32 sm:w-44" data-testid="status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="estimate">Estimate</SelectItem>
              <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          {/* Estimate Date - for backdating invoices */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Est:</Label>
            <Input
              type="date"
              value={invoice.estimate_date || ""}
              onChange={(e) => setInvoice(prev => ({ ...prev, estimate_date: e.target.value }))}
              className="h-9 w-32 sm:w-36 text-sm"
              placeholder="Estimate date"
              data-testid="estimate-date-input"
            />
          </div>
          {/* Paid Date - shown when status is paid */}
          {invoice.status === "paid" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Paid:</Label>
              <Input
                type="date"
                value={invoice.paid_date || ""}
                onChange={(e) => setInvoice(prev => ({ ...prev, paid_date: e.target.value }))}
                className="h-9 w-32 sm:w-36 text-sm"
                data-testid="paid-date-input"
              />
            </div>
          )}
          <Button 
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={!id}
            className="gap-1 sm:gap-2 px-3"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Button 
            onClick={() => handleSave(false)}
            disabled={saving}
            className="gap-1 sm:gap-2 bg-blue-600 hover:bg-blue-700 px-3"
            data-testid="save-invoice-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span className="hidden sm:inline">Save</span>
          </Button>
        </div>
      </div>

      {/* Invoice Paper - Responsive */}
      <div className="invoice-paper max-w-7xl mx-auto bg-white rounded-xl shadow-xl p-4 sm:p-6 lg:p-10">
        {/* Section 1: Company Header */}
        <div className="text-center border-b-2 border-blue-600 pb-4 sm:pb-6 mb-4 sm:mb-6">
          <img 
            src={settings.logo_url || DEFAULT_LOGO_URL} 
            alt="Company Logo" 
            className="max-h-20 sm:max-h-28 mx-auto object-contain"
            data-testid="company-logo"
          />
        </div>

        {/* Invoice Number Display */}
        {invoice.invoice_number && (
          <div className="text-right mb-4">
            <span className="text-2xl font-bold text-blue-600 font-mono" data-testid="invoice-number">
              Invoice #{invoice.invoice_number}
            </span>
          </div>
        )}

        {/* Section 2: Customer & Vehicle Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Customer Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-sm sm:text-base font-semibold text-blue-700">
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Select Customer</Label>
                <Select 
                  value={invoice.customer_id} 
                  onValueChange={handleCustomerSelect}
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Name</Label>
                  <Input 
                    value={invoice.customer_name}
                    onChange={(e) => setInvoice(prev => ({ ...prev, customer_name: e.target.value }))}
                    className="h-9"
                    data-testid="customer-name-display"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <Input 
                    value={invoice.customer_phone}
                    onChange={(e) => setInvoice(prev => ({ ...prev, customer_phone: e.target.value }))}
                    className="h-9"
                    data-testid="customer-phone-display"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Address</Label>
                <Input 
                  value={invoice.customer_address}
                  onChange={(e) => setInvoice(prev => ({ ...prev, customer_address: e.target.value }))}
                  className="h-9"
                  data-testid="customer-address-display"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">2nd Authorized Contact</Label>
                <Input 
                  value={invoice.second_contact}
                  onChange={(e) => setInvoice(prev => ({ ...prev, second_contact: e.target.value }))}
                  className="h-9"
                  data-testid="second-contact-display"
                />
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-sm sm:text-base font-semibold text-blue-700">
                Vehicle / Job Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3">
              {/* Vehicle Quick Select */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-xs text-slate-600 font-medium mb-2 block">
                  {invoice.customer_id ? `${invoice.customer_name?.split(' ')[0]}'s Fleet` : "Quick Select Vehicle"}
                </Label>
                <div className="flex gap-2">
                  <Select value={selectedVehicleId} onValueChange={handleVehicleSelect}>
                    <SelectTrigger className="flex-1 h-9 text-sm" data-testid="vehicle-select">
                      <SelectValue placeholder={invoice.customer_id ? (vehicles.length > 0 ? "Select from fleet..." : "No vehicles yet - add one below") : "Select customer first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Manual Entry --</SelectItem>
                      {vehicles.map(vehicle => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          <span className="flex items-center gap-2">
                            <Truck className="h-3 w-3" />
                            {vehicle.truck_number ? `Truck #${vehicle.truck_number}` : `Trailer #${vehicle.trailer_number}`}
                            {vehicle.make && ` - ${vehicle.make}`}
                            {vehicle.model && ` ${vehicle.model}`}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedVehicleId && vehicleHistory.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryDialogOpen(true)}
                      className="gap-1 h-9"
                      data-testid="view-history-btn"
                    >
                      <History className="h-4 w-4" />
                      <span className="hidden sm:inline">History ({vehicleHistory.length})</span>
                    </Button>
                  )}
                </div>
                {selectedVehicleId && vehicleHistory.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    Last service: {formatHistoryDate(vehicleHistory[0]?.created_at)} - {vehicleHistory[0]?.items?.[0]?.parts_description || vehicleHistory[0]?.items?.[0]?.labor_description || "Service"}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Truck #</Label>
                  <Input 
                    value={invoice.vehicle_info.truck_number}
                    onChange={(e) => handleVehicleChange("truck_number", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="truck-number-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Trailer #</Label>
                  <Input 
                    value={invoice.vehicle_info.trailer_number}
                    onChange={(e) => handleVehicleChange("trailer_number", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="trailer-number-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Make</Label>
                  <Input 
                    value={invoice.vehicle_info.make}
                    onChange={(e) => handleVehicleChange("make", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="make-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Model</Label>
                  <Input 
                    value={invoice.vehicle_info.type_model}
                    onChange={(e) => handleVehicleChange("type_model", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="model-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Year</Label>
                  <Input 
                    value={invoice.vehicle_info.year}
                    onChange={(e) => handleVehicleChange("year", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="year-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Serial / VIN #</Label>
                  <Input 
                    value={invoice.vehicle_info.serial_vin}
                    onChange={(e) => handleVehicleChange("serial_vin", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="vin-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">License #</Label>
                  <Input 
                    value={invoice.vehicle_info.license_number}
                    onChange={(e) => handleVehicleChange("license_number", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="license-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Odometer</Label>
                  <Input 
                    value={invoice.vehicle_info.odometer}
                    onChange={(e) => handleVehicleChange("odometer", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="odometer-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Written By</Label>
                  <Input 
                    value={invoice.vehicle_info.order_written_by}
                    onChange={(e) => handleVehicleChange("order_written_by", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="written-by-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Received</Label>
                  <Input 
                    type="date"
                    value={invoice.vehicle_info.received_date}
                    onChange={(e) => handleVehicleChange("received_date", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="received-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Promised</Label>
                  <Input 
                    type="date"
                    value={invoice.vehicle_info.promised_date}
                    onChange={(e) => handleVehicleChange("promised_date", e.target.value)}
                    className="h-8 sm:h-9 text-sm"
                    data-testid="promised-date-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1 sm:pt-2">
                <Checkbox 
                  id="phone_when_ready"
                  checked={invoice.vehicle_info.phone_when_ready}
                  onCheckedChange={(checked) => handleVehicleChange("phone_when_ready", checked)}
                  data-testid="phone-when-ready-checkbox"
                />
                <Label htmlFor="phone_when_ready" className="text-xs sm:text-sm cursor-pointer">
                  Phone When Ready
                </Label>
              </div>
              
              {/* Save Vehicle Button */}
              {invoice.customer_id && (invoice.vehicle_info.truck_number || invoice.vehicle_info.trailer_number) && !selectedVehicleId && (
                <div className="pt-3 border-t border-slate-200 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveAsVehicle}
                    disabled={savingVehicle}
                    className="w-full gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="save-vehicle-btn"
                  >
                    {savingVehicle ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save as New Vehicle for {invoice.customer_name?.split(' ')[0] || 'Customer'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1 text-center">
                    Add this vehicle to the customer's fleet for future invoices
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Section 3: Parts & Labor Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Parts Table */}
          <div className="parts-table rounded-lg overflow-hidden border border-blue-100" data-testid="parts-table">
            <div className="bg-blue-600 text-white px-3 sm:px-4 py-2 font-semibold text-sm sm:text-base">
              PARTS
            </div>
            <div className="overflow-x-auto">
              <table className="w-full invoice-table text-sm">
                <thead>
                  <tr className="bg-blue-100/70 text-blue-900 text-xs sm:text-sm">
                    <th className="px-2 sm:px-3 py-2 text-left w-12 sm:w-16">Qty</th>
                    <th className="px-2 sm:px-3 py-2 text-left">Description</th>
                    <th className="px-2 sm:px-3 py-2 text-right w-16 sm:w-24">Price</th>
                    <th className="px-2 sm:px-3 py-2 text-right w-16 sm:w-24">Total</th>
                    <th className="px-1 sm:px-3 py-2 w-8 sm:w-10 no-print"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={item.id || index} className="border-b border-blue-100 align-top">
                      <td className="px-1 sm:px-2 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={item.parts_qty || ""}
                          onChange={(e) => handleItemChange(index, "parts_qty", e.target.value)}
                          onBlur={() => handleItemBlur(index, "parts_qty")}
                          className="h-7 sm:h-8 w-10 sm:w-14 text-center p-1 text-xs sm:text-sm"
                          data-testid={`parts-qty-${index}`}
                        />
                      </td>
                      <td className="px-1 sm:px-2 py-2">
                        <textarea
                          value={item.parts_description}
                          onChange={(e) => handleItemChange(index, "parts_description", e.target.value)}
                          className="w-full min-h-[36px] sm:min-h-[40px] px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          placeholder="Part description"
                          rows={2}
                          data-testid={`parts-desc-${index}`}
                        />
                      </td>
                      <td className="px-1 sm:px-2 py-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.parts_price === 0 || item.parts_price === "0" ? "" : item.parts_price}
                          onChange={(e) => handleItemChange(index, "parts_price", e.target.value)}
                          onBlur={() => handleItemBlur(index, "parts_price")}
                          className="h-7 sm:h-8 text-right w-14 sm:w-20 text-xs sm:text-sm"
                          placeholder="0.00"
                          data-testid={`parts-price-${index}`}
                        />
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm">
                        ${((parseFloat(item.parts_qty) || 0) * (parseFloat(item.parts_price) || 0)).toFixed(2)}
                      </td>
                      <td className="px-0.5 sm:px-1 py-2 no-print">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-red-500 hover:text-red-700"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-100/50 font-semibold">
                    <td colSpan="2" className="px-2 sm:px-3 py-2 text-left text-xs sm:text-sm" data-testid="parts-tax">
                      Tax Total: <span className="font-mono">${taxAmount.toFixed(2)}</span>
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm">Total Parts:</td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm" data-testid="total-parts">
                      ${totalParts.toFixed(2)}
                    </td>
                    <td className="no-print"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Labor Table */}
          <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="labor-table">
            <div className="bg-slate-700 text-white px-3 sm:px-4 py-2 font-semibold text-sm sm:text-base">
              LABOR
            </div>
            <div className="overflow-x-auto">
              <table className="w-full invoice-table text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 text-xs sm:text-sm">
                    <th className="px-2 sm:px-3 py-2 text-left w-12 sm:w-16">Qty</th>
                    <th className="px-2 sm:px-3 py-2 text-left">Description</th>
                    <th className="px-2 sm:px-3 py-2 text-right w-16 sm:w-24">Price</th>
                    <th className="px-2 sm:px-3 py-2 text-right w-16 sm:w-24">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={item.id || index} className="border-b border-slate-100 align-top">
                      <td className="px-1 sm:px-2 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={item.labor_qty || ""}
                          onChange={(e) => handleItemChange(index, "labor_qty", e.target.value)}
                          className="h-7 sm:h-8 w-10 sm:w-14 text-center p-1 text-xs sm:text-sm"
                          data-testid={`labor-qty-${index}`}
                        />
                      </td>
                      <td className="px-1 sm:px-2 py-2">
                        <textarea
                          value={item.labor_description}
                          onChange={(e) => handleItemChange(index, "labor_description", e.target.value)}
                          className="w-full min-h-[36px] sm:min-h-[40px] px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500"
                          placeholder="Labor description"
                          rows={2}
                          data-testid={`labor-desc-${index}`}
                        />
                      </td>
                      <td className="px-1 sm:px-2 py-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.labor_price === 0 || item.labor_price === "0" ? "" : item.labor_price}
                          onChange={(e) => handleItemChange(index, "labor_price", e.target.value)}
                          onBlur={() => handleItemBlur(index, "labor_price")}
                          className="h-7 sm:h-8 text-right w-14 sm:w-20 text-xs sm:text-sm"
                          placeholder="0.00"
                          data-testid={`labor-price-${index}`}
                        />
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm">
                        ${((item.labor_qty || 0) * (item.labor_price || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100/50 font-semibold">
                    <td colSpan="3" className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm">Total Labor:</td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm" data-testid="total-labor">
                      ${totalLabor.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Add Row Button */}
        <div className="no-print mb-6 sm:mb-8">
          <Button 
            variant="outline" 
            onClick={addItem}
            className="gap-2 text-sm"
            data-testid="add-row-btn"
          >
            <Plus className="h-4 w-4" />
            Add Row
          </Button>
        </div>

        {/* Notes Section */}
        <div className="mb-6 sm:mb-8">
          <Card className="border-slate-200" data-testid="notes-section">
            <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-4">
              <CardTitle className="text-sm sm:text-base font-semibold text-slate-700">
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <textarea
                value={invoice.notes || ""}
                onChange={(e) => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full min-h-[60px] sm:min-h-[80px] px-3 py-2 text-xs sm:text-sm border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="Add any additional notes..."
                rows={3}
                data-testid="notes-input"
              />
            </CardContent>
          </Card>
        </div>

        {/* Section 4: Totals & Authorization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Totals */}
          <Card className="border-slate-200" data-testid="totals-section">
            <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-4">
              <CardTitle className="text-sm sm:text-base font-semibold text-slate-700">
                Invoice Totals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-4 pt-0">
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <span className="text-xs sm:text-sm text-slate-600">Total Parts</span>
                <span className="font-mono font-semibold text-sm">${totalParts.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <span className="text-xs sm:text-sm text-slate-600">Total Labor</span>
                <span className="font-mono font-semibold text-sm">${totalLabor.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <span className="text-xs sm:text-sm text-slate-600">Road Service</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={invoice.road_service_fee === 0 ? "" : invoice.road_service_fee}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
                      setInvoice(prev => ({ ...prev, road_service_fee: value === "" ? 0 : value }));
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = parseFloat(e.target.value) || 0;
                    setInvoice(prev => ({ ...prev, road_service_fee: numValue }));
                  }}
                  className="h-7 sm:h-8 w-20 sm:w-28 text-right font-mono text-xs sm:text-sm"
                  placeholder="0.00"
                  data-testid="road-service-fee-input"
                />
              </div>
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <span className="text-xs sm:text-sm text-slate-600">Storage Fee</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={invoice.storage_fee === 0 ? "" : invoice.storage_fee}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
                      setInvoice(prev => ({ ...prev, storage_fee: value === "" ? 0 : value }));
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = parseFloat(e.target.value) || 0;
                    setInvoice(prev => ({ ...prev, storage_fee: numValue }));
                  }}
                  className="h-7 sm:h-8 w-20 sm:w-28 text-right font-mono text-xs sm:text-sm"
                  placeholder="0.00"
                  data-testid="storage-fee-input"
                />
              </div>
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <span className="text-xs sm:text-sm text-slate-600">Subtotal</span>
                <span className="font-mono font-semibold text-sm">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-slate-100">
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm text-slate-600">Tax (Parts Only)</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={invoice.tax_rate === 0 ? "" : invoice.tax_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
                        setInvoice(prev => ({ ...prev, tax_rate: value === "" ? 0 : value }));
                      }
                    }}
                    onBlur={(e) => {
                      const numValue = parseFloat(e.target.value) || 0;
                      setInvoice(prev => ({ ...prev, tax_rate: numValue }));
                    }}
                    className="h-6 sm:h-7 w-12 sm:w-16 text-center text-xs sm:text-sm"
                    placeholder="0"
                    data-testid="tax-rate-input"
                  />
                  <span className="text-slate-500 text-xs sm:text-sm">%</span>
                </div>
                <span className="font-mono font-semibold text-sm">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-3 bg-blue-600 text-white rounded-lg px-3 sm:px-4 -mx-3 sm:-mx-4 mt-3 sm:mt-4">
                <span className="font-bold text-sm sm:text-lg">TOTAL DUE</span>
                <span className="font-mono font-bold text-lg sm:text-2xl" data-testid="total-due">
                  ${totalDue.toFixed(2)}
                </span>
              </div>
              
              {/* Payment Method */}
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200">
                <Label className="text-xs sm:text-sm font-semibold text-slate-700 mb-2 block">
                  Payment Method
                </Label>
                <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-1.5 sm:gap-2 mb-2">
                  {['cash', 'check', 'card', 'zelle'].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setInvoice(prev => ({ 
                        ...prev, 
                        payment_method: prev.payment_method === method ? '' : method,
                        payment_method_other: prev.payment_method === method ? prev.payment_method_other : ''
                      }))}
                      className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all ${
                        invoice.payment_method === method
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                      }`}
                      data-testid={`payment-${method}`}
                    >
                      {method.toUpperCase()}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setInvoice(prev => ({ 
                      ...prev, 
                      payment_method: prev.payment_method === 'other' ? '' : 'other'
                    }))}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all col-span-2 sm:col-span-1 ${
                      invoice.payment_method === 'other'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                    }`}
                    data-testid="payment-other"
                  >
                    OTHER
                  </button>
                </div>
                {invoice.payment_method === 'other' && (
                  <Input
                    value={invoice.payment_method_other || ''}
                    onChange={(e) => setInvoice(prev => ({ ...prev, payment_method_other: e.target.value }))}
                    placeholder="Enter custom payment method"
                    className="mt-2 text-sm"
                    data-testid="payment-other-input"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Signature */}
          <Card className="border-slate-200" data-testid="signature-section">
            <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-4">
              <CardTitle className="text-sm sm:text-base font-semibold text-slate-700">
                Customer Authorization
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <Label className="text-xs sm:text-sm text-slate-500 mb-2 block">
                    Customer Signature
                  </Label>
                  <div className="border border-slate-300 rounded-lg bg-white">
                    <SignatureCanvas
                      ref={signatureRef}
                      penColor="#1e293b"
                      canvasProps={{
                        className: "signature-canvas w-full h-24 sm:h-32",
                        "data-testid": "signature-canvas"
                      }}
                      onEnd={() => {
                        if (signatureRef.current && !signatureRef.current.isEmpty()) {
                          setInvoice(prev => ({
                            ...prev,
                            signature_data: signatureRef.current.toDataURL(),
                            signature_date: prev.signature_date || new Date().toISOString().split('T')[0]
                          }));
                        }
                      }}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="mt-2 gap-1 text-slate-500 text-xs sm:text-sm h-8"
                    data-testid="clear-signature-btn"
                  >
                    <Eraser className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Clear Signature
                  </Button>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm text-slate-500">Signature Date</Label>
                  <Input
                    type="date"
                    value={invoice.signature_date}
                    onChange={(e) => setInvoice(prev => ({ ...prev, signature_date: e.target.value }))}
                    className="mt-1 text-sm"
                    data-testid="signature-date-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Vehicle History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Repair History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {vehicleHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No repair history found</p>
            ) : (
              <div className="space-y-3">
                {vehicleHistory.map((historyInvoice) => (
                  <div key={historyInvoice.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-blue-600">{historyInvoice.invoice_number}</p>
                        <p className="text-sm text-slate-600">{historyInvoice.customer_name}</p>
                        <p className="text-xs text-slate-400">{formatHistoryDate(historyInvoice.created_at)}</p>
                      </div>
                      <Badge variant={historyInvoice.status === "paid" ? "default" : "secondary"} className={
                        historyInvoice.status === "paid" 
                          ? "bg-emerald-100 text-emerald-700"
                          : historyInvoice.status === "awaiting_payment"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                      }>
                        {historyInvoice.status === "paid" ? "Paid" : historyInvoice.status === "awaiting_payment" ? "Awaiting Payment" : "Estimate"}
                      </Badge>
                    </div>
                    {historyInvoice.items?.length > 0 && (
                      <div className="mt-2 text-sm text-slate-600">
                        {historyInvoice.items.slice(0, 3).map((item, idx) => (
                          <p key={idx} className="truncate">
                            • {item.parts_description || item.labor_description}
                          </p>
                        ))}
                        {historyInvoice.items.length > 3 && (
                          <p className="text-slate-400">+{historyInvoice.items.length - 3} more items</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
